import { Flex } from 'antd'
import type { FunctionComponent } from 'react'
import styled from 'styled-components'
import { useDeleteCollaborationRecordScoringMutation } from '@/api/workspace-api/directory-api'
import ScoringView, {
  type ScoringViewProps,
} from '@/components/kol/common/kol-cooperation-detail-drawer/cooperation-history/scoring-view'
import useNotification from '@/hooks/antd/use-notification'
import { useIntl } from '@/i18n/hooks/use-intl'
import type {
  CollaborationRecordScoringType,
  Scoring,
} from '@/types/schema/directory/collaboration-record'
import logger from '@/utils/logger'

interface LegacyScoringListProps {
  kolId: string
  collaborationRecordScoringType: CollaborationRecordScoringType
  data: Scoring[]
}

const LegacyScoringList: FunctionComponent<LegacyScoringListProps> = ({
  kolId,
  collaborationRecordScoringType,
  data,
}) => {
  const { formatMessage } = useIntl()
  const notification = useNotification()

  const [deleteCollaborationRecordScoring] =
    useDeleteCollaborationRecordScoringMutation()

  const handleDelete: ScoringViewProps['onDeleteScoring'] = async (
    scoring,
  ): Promise<void> => {
    try {
      await deleteCollaborationRecordScoring({
        kolId,
        collaborationRecordScoringId: scoring.id,
        collaborationRecordScoringType,
      }).unwrap()

      notification.success({
        message: formatMessage({
          id: 'general:operation_success',
        }),
        placement: 'bottomRight',
      })
    } catch (error) {
      logger.error(error)

      notification.error({
        message: formatMessage({
          id: 'general:operation_failure',
        }),
        placement: 'bottomRight',
      })
    }
  }

  return (
    <Flex vertical gap={16}>
      <LegacyCollaborationRecordTitle>
        {formatMessage({
          id: 'kol_cooperation_detail_drawer:collaboration_record_list_legacy_title',
        })}
      </LegacyCollaborationRecordTitle>
      {data.map((scoring) => (
        <ScoringView
          key={scoring.id}
          scoring={scoring}
          style='legacy'
          onDeleteScoring={handleDelete}
        />
      ))}
    </Flex>
  )
}

const LegacyCollaborationRecordTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 16px;
  font-weight: 700;
  line-height: 140%;
`

export default LegacyScoringList
